// ── FCM messaging helpers ─────────────────────────────────────────────
//
// Wraps the Firebase Cloud Messaging client SDK. Used by Settings.js
// to register/deregister push tokens when the user toggles push
// notifications on or off.
//
// Token storage: users/{uid}/fcmTokens/{tokenId}
//   - tokenId is the FCM token itself (URL-safe, unique)
//   - one doc per browser/device install of the PWA
//   - Cloud Function (processAlerts) reads this subcollection on alert
//     fire and pushes to all tokens; deletes invalid ones on send fail
//
// Browser support note: getToken() requires an active service worker.
// On iOS Safari, push only works when the PWA is installed to the
// home screen (iOS 16.4+). In a regular Safari tab, getToken() will
// return null or throw — handle gracefully in the UI.

import { getMessaging, getToken, deleteToken, isSupported } from 'firebase/messaging';
import { doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { app, db } from './firebase';

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

// Detect platform for the token doc — useful for debugging which
// devices a user has registered. Best-effort; navigator.userAgent
// strings are wobbly across browsers.
function detectPlatform() {
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'web';
}

// Returns true if this browser can receive push notifications at all.
// Wraps Firebase's isSupported() check, which probes for Notification API,
// service worker support, and PushManager availability.
export async function isPushSupported() {
  try {
    return await isSupported();
  } catch {
    return false;
  }
}

// Request permission, fetch token, save to Firestore.
// Returns the token on success, null on failure (permission denied,
// unsupported browser, or SW not ready). Caller decides UX.
export async function enablePush(uid) {
  if (!uid) throw new Error('enablePush requires uid');
  if (!VAPID_KEY) {
    console.error('[FCM] REACT_APP_FIREBASE_VAPID_KEY not set');
    return null;
  }

  const supported = await isPushSupported();
  if (!supported) {
    console.warn('[FCM] Push not supported on this browser');
    return null;
  }

  // Notification.requestPermission resolves to 'granted', 'denied',
  // or 'default' (user dismissed without choosing). Only 'granted'
  // lets us proceed.
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    console.log(`[FCM] Permission ${permission}, aborting`);
    return null;
  }

  // The SW registration must already exist (registered by
  // serviceWorkerRegistration.js at app boot). We pass it explicitly
  // so FCM uses our custom workbox SW rather than trying to register
  // its own firebase-messaging-sw.js.
  const swReg = await navigator.serviceWorker.ready;
  const messaging = getMessaging(app);

  let token;
  try {
    token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });
  } catch (err) {
    console.error('[FCM] getToken failed', err);
    return null;
  }

  if (!token) {
    console.warn('[FCM] getToken returned empty (permission may have been revoked)');
    return null;
  }

  // Save to Firestore. Token is the doc ID — setDoc with merge handles
  // the "already registered this token" case as a no-op overwrite.
  try {
    await setDoc(
      doc(db, 'users', uid, 'fcmTokens', token),
      {
        token,
        platform: detectPlatform(),
        userAgent: navigator.userAgent || '',
        createdAt: serverTimestamp(),
        lastUsedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (err) {
    console.error('[FCM] Saving token to Firestore failed', err);
    return null;
  }

  console.log('[FCM] Token registered');
  return token;
}

// Disable push: delete the FCM token from the SW and remove the doc
// from Firestore. Idempotent — safe to call even if push wasn't enabled.
export async function disablePush(uid) {
  if (!uid) return;

  const supported = await isPushSupported();
  if (!supported) return;

  // If notification permission was never granted in THIS browser, there is
  // no token to retrieve or delete — and calling getToken() below would
  // itself PROMPT for permission, which must never happen on a *disable*
  // action (that was the "turning it off asks to Allow" bug). Nothing to
  // do here; the caller still flips pushEnabled to false.
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    console.log('[FCM] Push disabled (no granted permission in this browser; nothing to revoke)');
    return;
  }

  const messaging = getMessaging(app);

  // Get the current token first so we know which doc to delete.
  // If no token exists (already disabled), this returns null and
  // we skip the rest.
  let token;
  try {
    token = await getToken(messaging, { vapidKey: VAPID_KEY });
  } catch {
    token = null;
  }

  if (token) {
    try {
      await deleteDoc(doc(db, 'users', uid, 'fcmTokens', token));
    } catch (err) {
      console.error('[FCM] Deleting Firestore token doc failed', err);
    }
    try {
      await deleteToken(messaging);
    } catch (err) {
      console.error('[FCM] deleteToken failed', err);
    }
  }

  console.log('[FCM] Push disabled');
}